import { isPlatformBrowser } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { FactoryProvider, makeStateKey, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import { map } from 'rxjs/operators';

import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { Request } from 'express';
import { REQUEST } from '../../express.tokens';

import { environment } from '../../environments/environment';
import possibleTypesResult from '../common/introspection-results';
import { getRequestBaseUrl } from '../common/utils/get-request-base-url';

const STATE_KEY = makeStateKey<any>('apollo.state');

export const APOLLO_CLIENT_PROVIDER: FactoryProvider = {
    provide: APOLLO_OPTIONS,
    useFactory: apolloOptionsFactory,
    deps: [HttpLink, PLATFORM_ID, TransferState, [new Optional(), REQUEST]]
};

function mergeFields(existing: any, incoming: any) {
    return { ...existing, ...incoming };
}

function relaceFields(existing: any, incoming: any) {
    return incoming;
}

// Trying to debug why sessions won't work in Safari 13.1
// but only on the live prod version.
function logInterceptorData(on: boolean) {
    localStorage.setItem('_logInterceptorData', on ? 'true' : 'false');
}

if (typeof window !== 'undefined') {
    (window as any).logInterceptorData = logInterceptorData;
}

export function apolloOptionsFactory(
    httpLink: HttpLink,
    platformId: object,
    transferState: TransferState,
    req?: Request
): ApolloClient.Options {
    const AUTH_TOKEN_KEY = 'auth_token';
    const isBrowser = isPlatformBrowser(platformId);
    const apolloCache = new InMemoryCache({
        // SSR renders are one-shot, so avoid retaining Apollo's result memoization tries between requests.
        resultCaching: isBrowser,
        possibleTypes: possibleTypesResult.possibleTypes,
        typePolicies: {
            Query: {
                fields: {
                    eligibleShippingMethods: {
                        merge: relaceFields
                    },
                    activeCustomerWishlists: {
                        merge: relaceFields,
                    },
                },
            },
            Product: {
                fields: {
                    customFields: {
                        merge: mergeFields
                    }
                }
            },
            Collection: {
                fields: {
                    customFields: {
                        merge: mergeFields
                    }
                }
            },
            Order: {
                fields: {
                    lines: {
                        merge: relaceFields
                    },
                    shippingLines: {
                        merge: relaceFields
                    },
                    discounts: {
                        merge: relaceFields
                    },
                    shippingAddress: {
                        merge: relaceFields
                    },
                    billingAddress: {
                        merge: relaceFields
                    }
                }
            },
            Customer: {
                fields: {
                    addresses: {
                        merge: relaceFields
                    },
                    customFields: {
                        merge: mergeFields
                    }
                }
            }
        }
    });

    const { shopApiPath } = environment;
    const uri = `${getRequestBaseUrl(platformId)}/${shopApiPath}`;
    const options = {
        uri,
        withCredentials: false
    };

    const afterware = new ApolloLink((operation, forward) => {
        return forward(operation).pipe(
            map((response: ApolloLink.Result) => {
                const context = operation.getContext();
                const authHeader = context['response'].headers.get('vendure-auth-token');
                if (authHeader && isBrowser) {
                    // If the auth token has been returned by the Vendure
                    // server, we store it in localStorage
                    localStorage.setItem(AUTH_TOKEN_KEY, authHeader);
                }
                return response;
            })
        );
    });
    const middleware = new ApolloLink((operation, forward) => {
        if (isBrowser) {
            operation.setContext({
                headers: new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY) || null}`)
            });
        }
        return forward(operation);
    });

    const hasSerializedState = transferState.hasKey<any>(STATE_KEY);

    if (hasSerializedState) {
        const state = transferState.get<any>(STATE_KEY, null);
        apolloCache.restore(state);
    } else {
        transferState.onSerialize(STATE_KEY, () => {
            const state = apolloCache.extract();
            apolloCache.reset();
            return state;
        });
    }

    const http = httpLink.create(options);

    return {
        cache: apolloCache,
        ssrMode: true,
        ssrForceFetchDelay: 500,
        link: ApolloLink.from([middleware, afterware, http])
    };
}
